/**
 * @fileoverview Which colour scheme the app is showing, so a theme's light or
 * dark colours can be picked to match.
 *
 * THE SAME RULE @cloistr/ui's stylesheet uses, read rather than re-decided.
 * Dark is the default. Light applies when the root carries
 * `data-theme="light"` (or the legacy `cloistr-light` class), or when the OS
 * prefers light and the app has not forced a theme. A different rule here
 * would put a theme's dark colours on the app's light background.
 */

import { useEffect, useState } from 'react';

import type { ColourScheme } from '@/services/pages/theme';

export function currentColourScheme(): ColourScheme {
  if (typeof document === 'undefined') return 'light';
  const root = document.documentElement;
  const forced = root.getAttribute('data-theme');
  if (forced === 'light' || root.classList.contains('cloistr-light')) return 'light';
  if (forced === 'dark') return 'dark';
  // No way to ask is not a browser at all (a test, a server render), and the
  // light values are the ones every theme has always meant.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** The scheme now, updated when the OS setting or the app's toggle changes. */
export function useColourScheme(): ColourScheme {
  const [scheme, setScheme] = useState<ColourScheme>(currentColourScheme);

  useEffect(() => {
    const update = () => setScheme(currentColourScheme());
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });
    const media =
      typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: light)') : null;
    media?.addEventListener('change', update);
    return () => {
      observer.disconnect();
      media?.removeEventListener('change', update);
    };
  }, []);

  return scheme;
}
